import apiClient from './api'

function cleanParams(params = {}) {
  const result = {}
  Object.entries(params).forEach(([key, value]) => {
    if (value == null) return
    if (typeof value === 'string' && value.trim() === '') return
    result[key] = value
  })
  return result
}

function asString(value, fallback = '') {
  if (value == null) return fallback
  return String(value)
}

function asNumber(value, fallback = null) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function normalizePolicy(row = {}) {
  return {
    id: asString(row.id),
    subjectType: asString(row.subjectType),
    subjectId: row.subjectId == null ? null : asString(row.subjectId),
    endpointGroup: asString(row.endpointGroup),
    windowSeconds: asNumber(row.windowSeconds, 3600),
    maxRequests: asNumber(row.maxRequests, 0),
    maxCostUnits: row.maxCostUnits == null ? null : asNumber(row.maxCostUnits, null),
    maxConcurrentJobs: row.maxConcurrentJobs == null ? null : asNumber(row.maxConcurrentJobs, null),
    dailyMaxRequests: row.dailyMaxRequests == null ? null : asNumber(row.dailyMaxRequests, null),
    monthlyMaxRequests: row.monthlyMaxRequests == null ? null : asNumber(row.monthlyMaxRequests, null),
    status: asString(row.status, 'active'),
    priority: asNumber(row.priority, 100),
    reason: row.reason == null ? null : asString(row.reason),
    createdByUserId: row.createdByUserId == null ? null : asString(row.createdByUserId),
    updatedByUserId: row.updatedByUserId == null ? null : asString(row.updatedByUserId),
    effectiveFrom: row.effectiveFrom || null,
    effectiveTo: row.effectiveTo || null,
    createdAt: row.createdAt || null,
    updatedAt: row.updatedAt || null,
  }
}

function normalizeIdentity(row = {}) {
  return {
    id: asString(row.id),
    provider: asString(row.provider, 'telegram'),
    providerUserId: asString(row.providerUserId),
    username: row.username == null ? null : asString(row.username),
    firstName: row.firstName == null ? null : asString(row.firstName),
    lastName: row.lastName == null ? null : asString(row.lastName),
    photoUrl: row.photoUrl == null ? null : asString(row.photoUrl),
    status: asString(row.status, 'active'),
    linkedUser: row.linkedUser || null,
    lastSeenAt: row.lastSeenAt || null,
    lastAuthAt: row.lastAuthAt || null,
    effectivePolicy: row.effectivePolicy ? normalizePolicy(row.effectivePolicy) : null,
    effectiveLimit: row.effectiveLimit || null,
  }
}

function normalizeDetail(row = {}) {
  return {
    identity: row.identity
      ? {
          ...normalizeIdentity(row.identity),
          linkedAt: row.identity.linkedAt || null,
          metadata: row.identity.metadata || null,
          createdAt: row.identity.createdAt || null,
          updatedAt: row.identity.updatedAt || null,
        }
      : null,
    linkedUser: row.linkedUser || null,
    effectivePolicy: row.effectivePolicy ? normalizePolicy(row.effectivePolicy) : null,
    effectiveLimit: row.effectiveLimit || null,
    usageBuckets: Array.isArray(row.usageBuckets) ? row.usageBuckets : [],
    usageSummary: row.usageSummary || {
      requestCount: 0,
      acceptedCount: 0,
      rejectedCount: 0,
      costUnits: 0,
    },
    recentPublicRecognitionCount: asNumber(row.recentPublicRecognitionCount, 0),
  }
}

function normalizeError(error, fallbackMessage) {
  return {
    status: Number(error?.response?.status || 0),
    message: error?.response?.data?.message || error?.message || fallbackMessage,
    code: error?.response?.data?.error || error?.response?.data?.code || null,
  }
}

export async function listExternalIdentities(params = {}) {
  try {
    const response = await apiClient.get('/admin/external-identities', { params: cleanParams(params) })
    const rows = Array.isArray(response?.data?.data) ? response.data.data : []
    return rows.map(normalizeIdentity)
  } catch (error) {
    throw normalizeError(error, 'Unable to load external identities')
  }
}

export async function getExternalIdentity(id) {
  const identityId = asString(id).trim()
  if (!identityId) throw { status: 400, message: 'Identity id is required' }

  try {
    const response = await apiClient.get(`/admin/external-identities/${identityId}`)
    return normalizeDetail(response?.data?.data || response?.data)
  } catch (error) {
    throw normalizeError(error, 'Unable to load external identity detail')
  }
}

export async function updateExternalIdentityStatus(id, status) {
  const identityId = asString(id).trim()
  if (!identityId) throw { status: 400, message: 'Identity id is required' }

  try {
    const response = await apiClient.patch(`/admin/external-identities/${identityId}/status`, { status })
    return normalizeIdentity(response?.data?.data || response?.data)
  } catch (error) {
    throw normalizeError(error, 'Unable to update external identity status')
  }
}

export async function updateExternalIdentityLimits(id, payload) {
  const identityId = asString(id).trim()
  if (!identityId) throw { status: 400, message: 'Identity id is required' }

  try {
    const response = await apiClient.put(`/admin/external-identities/${identityId}/limits`, payload)
    return normalizePolicy(response?.data?.data || response?.data)
  } catch (error) {
    throw normalizeError(error, 'Unable to update external identity limits')
  }
}

export { normalizeIdentity, normalizeDetail, normalizePolicy }
